import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import { DisplayStatus } from "../items";
import { COPENHAGEN_TIME_NOTE } from "../../common/copenhagenTime";
import {
  ATTENTION, STATUSES, STATUS_HINT, cph, duration, copenhagenMidnight, nextDay,
} from "../../next/pages/DeviceJobsShared";

// Device jobs — bookings/cancellations that run on a physical device (the official
// Playtomic app on an iPhone) and the runners next to the devices. Classic twin of
// /next/device-jobs, same API, read-only. Unknown is the state that needs a person:
// a runner took the job and never reported, so the booking may exist on the venue.

const REFRESH_MS = 15000;
const PER_PAGE = 50;

export const STATUS_COLORS = [
  { value: "Queued", color: "blue" },
  { value: "Claimed", color: "blue" },
  { value: "Succeeded", color: "green" },
  { value: "Failed", color: "red" },
  { value: "Expired", color: "yellow" },
  { value: "Unknown", color: "red" },
];

export const JobStatus = ({ status }) => (
  <span title={STATUS_HINT[status]}>
    <DisplayStatus mapping={STATUS_COLORS}>{status}</DisplayStatus>
  </span>
);

JobStatus.propTypes = { status: PropTypes.string };

const cell = (label, content, props = {}) => (
  <td {...props}>
    <strong className="table-cell-label">{label}</strong>
    {content}
  </td>
);

// The classic stylesheet centres bare <label>s as blocks: plain divs keep the filter row tidy.
const Filter = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: ".25rem" }}>
    <span style={{ fontSize: ".8rem", color: "#666" }}>{label}</span>
    {children}
  </div>
);

Filter.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const DeviceJobsInternal = ({ api }) => {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const action = params.get("action") || "";
  const signupId = params.get("signupId") || "";
  const integrationId = params.get("integrationId") || "";
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const [jobs, setJobs] = useState(null);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [runners, setRunners] = useState(null);
  const [failed, setFailed] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loadedAt, setLoadedAt] = useState(null);

  const setQuery = (changes) => {
    const next = Object.fromEntries(params.entries());
    Object.assign(next, changes);
    if (!("page" in changes)) delete next.page;
    Object.keys(next).forEach((key) => !next[key] && delete next[key]);
    setParams(next);
  };

  const load = useCallback(async () => {
    const filters = {
      Status: status || undefined,
      Action: action || undefined,
      SignupId: signupId || undefined,
      IntegrationId: integrationId || undefined,
      From: copenhagenMidnight(from),
      To: to ? copenhagenMidnight(nextDay(to)) : undefined,
      Page: page,
      Count: PER_PAGE,
    };

    try {
      const [list, counts, runnerList] = await Promise.all([
        api.deviceJobs.searchDeviceJobs(filters),
        api.deviceJobs.getDeviceJobSummary(),
        api.deviceJobs.getDeviceRunners(),
      ]);
      setJobs(list?.items ?? []);
      setTotal(list?.totalCount ?? 0);
      setSummary(counts ?? null);
      setRunners(Array.isArray(runnerList) ? runnerList : []);
      setFailed(null);
      setLoadedAt(new Date());
    } catch (error) {
      setFailed(error?.message || "Could not load the device jobs.");
    }
  }, [api, status, action, signupId, integrationId, from, to, page]);

  useEffect(() => {
    setJobs(null);
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const counts = summary?.countsByStatus || {};
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const filtered = !!(status || action || signupId || integrationId || from || to);

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <PageTop documentTitle="Device jobs" documentDescription="Bookings that run on a device" />
          <h2 className="main-page-header">Device jobs</h2>
          <p>
            Bookings and cancellations that run on a phone, through the venue&apos;s official app.
            {loadedAt && ` Updated ${loadedAt.toLocaleTimeString("en-GB", { timeZone: "Europe/Copenhagen" })}.`}
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
            <button type="button" className="btn btn-secondary" onClick={load}>Refresh</button>
            <span style={{ display: "inline-flex", alignItems: "center", gap: ".4rem" }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ width: "auto", margin: 0 }}
              />
              <span onClick={() => setAutoRefresh(!autoRefresh)} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>Auto-refresh (15 s)</span>
            </span>
          </div>

          {failed && <div className="alert alert-danger">{failed}</div>}

          {counts.Unknown > 0 && (
            <div className="alert alert-warning">
              <strong>{counts.Unknown} job{counts.Unknown === 1 ? "" : "s"} Unknown.</strong>{" "}
              {STATUS_HINT.Unknown}{" "}
              {status !== "Unknown" && (
                <button type="button" className="btn btn-sm btn-warning" onClick={() => setQuery({ status: "Unknown" })}>
                  Show them
                </button>
              )}
            </div>
          )}

          {/* Counters per status, the ones that may need a person first. Click to filter. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: ".75rem", marginBottom: "1.5rem" }}>
            {[...ATTENTION, ...STATUSES.filter((x) => !ATTENTION.includes(x))].map((name) => {
              const value = counts[name] ?? 0;
              const alarming = value > 0 && (name === "Unknown" || name === "Queued");
              return (
                <button
                  type="button"
                  key={name}
                  title={STATUS_HINT[name]}
                  onClick={() => setQuery({ status: status === name ? "" : name })}
                  style={{
                    minWidth: "8rem",
                    padding: ".6rem .9rem",
                    textAlign: "left",
                    background: "#fff",
                    cursor: "pointer",
                    border: status === name ? "2px solid #333" : "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  <div style={{ fontSize: ".8rem", color: "#666" }}>{name}</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: alarming ? "#b00" : undefined }}>
                    {summary ? value.toLocaleString() : "—"}
                  </div>
                  <div style={{ fontSize: ".75rem", color: "#888" }}>
                    {name === "Queued" && summary?.oldestQueuedAt ? `oldest ${cph(summary.oldestQueuedAt)}` : "all time"}
                  </div>
                </button>
              );
            })}
          </div>

          <h3>Runners</h3>
          {summary && (
            <p style={{ color: "#666" }}>
              Offline after {summary.runnerOfflineAfterMinutes} min without polling, unless running a job
              (run timeout {summary.runTimeoutMinutes} min, queue timeout {summary.queueTimeoutMinutes} min).
            </p>
          )}
          <article className="data-table-wrap">
            <table className="data-lists component-data-table cell-content-align-top">
              <thead>
                <tr>
                  <td>Runner</td>
                  <td>State</td>
                  <td>Last seen</td>
                  <td>Running</td>
                  <td>Scripts</td>
                  <td>Version</td>
                </tr>
              </thead>
              <tbody>
                {runners === null && <tr><td colSpan={6}>Loading…</td></tr>}
                {runners !== null && runners.length === 0 && (
                  <tr><td colSpan={6}>No runner has ever polled the queue.</td></tr>
                )}
                {(runners ?? []).map((runner) => (
                  <tr key={runner.name}>
                    {cell("Runner", <strong>{runner.name}</strong>)}
                    {cell("State", (
                      <DisplayStatus mapping={[{ value: runner.online ? (runner.runningJobId ? "Online · busy" : "Online") : "Offline", color: runner.online ? "green" : "gray" }]}>
                        {runner.online ? (runner.runningJobId ? "Online · busy" : "Online") : "Offline"}
                      </DisplayStatus>
                    ))}
                    {cell("Last seen", cph(runner.lastSeenAt))}
                    {cell("Running", runner.runningJobId
                      ? <Link to={`/device-job/${runner.runningJobId}`}>since {cph(runner.runningSince)}</Link>
                      : "—")}
                    {cell("Scripts", (runner.scripts || []).join(", ") || "—")}
                    {cell("Version", runner.version || "—")}
                  </tr>
                ))}
              </tbody>
            </table>
          </article>

          <br />
          <h3>Jobs</h3>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "1rem", marginBottom: "1rem" }}>
            <Filter label="Status">
              <select className="form-control" value={status} onChange={(e) => setQuery({ status: e.target.value })}>
                <option value="">All</option>
                {STATUSES.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </Filter>
            <Filter label="Action">
              <select className="form-control" value={action} onChange={(e) => setQuery({ action: e.target.value })}>
                <option value="">All</option>
                <option value="Book">Book</option>
                <option value="Cancel">Cancel</option>
              </select>
            </Filter>
            <Filter label="Signup id">
              <input
                className="form-control"
                style={{ minWidth: "20rem" }}
                defaultValue={signupId}
                key={`s${signupId}`}
                placeholder="Paste a signup id"
                onBlur={(e) => e.target.value.trim() !== signupId && setQuery({ signupId: e.target.value.trim() })}
              />
            </Filter>
            <Filter label="Integration id">
              <input
                className="form-control"
                style={{ minWidth: "20rem" }}
                defaultValue={integrationId}
                key={`i${integrationId}`}
                placeholder="Paste an integration id"
                onBlur={(e) => e.target.value.trim() !== integrationId && setQuery({ integrationId: e.target.value.trim() })}
              />
            </Filter>
            <Filter label="Created from">
              <input className="form-control" type="date" value={from} onChange={(e) => setQuery({ from: e.target.value })} />
            </Filter>
            <Filter label="Created to">
              <input className="form-control" type="date" value={to} onChange={(e) => setQuery({ to: e.target.value })} />
            </Filter>
            {filtered && (
              <button type="button" className="btn btn-secondary" onClick={() => setParams({})}>Clear</button>
            )}
          </div>

          <p style={{ color: "#666" }}>
            {jobs === null ? "Loading…" : `${total.toLocaleString()} ${filtered ? "matching" : "in total"}`}
          </p>

          <article className="data-table-wrap">
            <table className="data-lists component-data-table cell-content-align-top">
              <thead>
                <tr>
                  <td>Created</td>
                  <td>Status</td>
                  <td>Action</td>
                  <td>Venue</td>
                  <td>Booking</td>
                  <td>Runner</td>
                  <td>Wait / run</td>
                  <td>Reference / error</td>
                </tr>
              </thead>
              <tbody>
                {jobs !== null && jobs.length === 0 && (
                  <tr><td colSpan={8}>{filtered ? "No job matches these filters." : "No device job yet."}</td></tr>
                )}
                {(jobs ?? []).map((job) => (
                  <tr key={job.id} style={job.status === "Unknown" ? { backgroundColor: "#FDECEC" } : undefined}>
                    {cell("Created", <Link to={`/device-job/${job.id}`}>{cph(job.createdAt)}</Link>, { style: { whiteSpace: "nowrap" } })}
                    {cell("Status", <JobStatus status={job.status} />)}
                    {cell("Action", job.action)}
                    {cell("Venue", (
                      <>
                        {job.facilityName || job.productName || "—"}
                        <div style={{ fontSize: ".8rem", color: "#666" }}>
                          {[job.facilityName && job.productName, job.systemType || job.script].filter(Boolean).join(" · ")}
                        </div>
                      </>
                    ))}
                    {cell("Booking", job.signupId ? (
                      <>
                        <Link to={`/signup/${job.signupId}`}>{job.activityStartsAt ? cph(job.activityStartsAt) : "Signup"}</Link>
                        <div style={{ fontSize: ".8rem", color: "#666", wordBreak: "break-all" }}>{job.userEmail || ""}</div>
                      </>
                    ) : "—")}
                    {cell("Runner", job.claimedBy || "—")}
                    {cell("Wait / run", `${duration(job.createdAt, job.claimedAt) || "—"} / ${duration(job.claimedAt, job.finishedAt) || "—"}`, { style: { whiteSpace: "nowrap" } })}
                    {cell("Reference / error", job.error
                      ? <span title={job.error} style={{ color: "#8A1C1C", wordBreak: "break-word" }}>{job.error.length > 120 ? `${job.error.slice(0, 120)}…` : job.error}</span>
                      : (job.reference || "—"))}
                  </tr>
                ))}
              </tbody>
            </table>
          </article>

          {pages > 1 && (
            <div className="form-inline" style={{ marginTop: "1rem", gap: ".75rem" }}>
              <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) })}>
                ← Previous
              </button>
              <span style={{ margin: "0 1rem" }}>Page {page} of {pages}</span>
              <button type="button" className="btn btn-secondary" disabled={page >= pages} onClick={() => setQuery({ page: String(page + 1) })}>
                Next →
              </button>
            </div>
          )}

          <p style={{ color: "#888", marginTop: "1.5rem" }}>{COPENHAGEN_TIME_NOTE}</p>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

DeviceJobsInternal.propTypes = {
  api: PropTypes.object.isRequired,
};

const DeviceJobs = withApi(DeviceJobsInternal);

DeviceJobs.requiresLogin = true;

export default DeviceJobs;
